import React from 'react';
import { Zap } from 'lucide-react';
import { useVolumeShockerSymbols } from '../../context/VolumeShockerContext';
import { useFnoSymbols } from '../../hooks/useFnoSymbols';
import { useNifty500Symbols } from '../../hooks/useNifty500Symbols';
import { useStockCatalysts } from '../../hooks/useStockCatalysts';
import { useStockDealBadge } from '../../hooks/useStockBulkBlockDeals';
import { StockCatalystSummary } from '../../types';

import { StockEmaPosture } from '../../utils/styleUtils';

export const getCatalystBadgeInfo = (catalyst: StockCatalystSummary): { label: string; icon: string } => {
  const sub = (catalyst.SubCategory || catalyst.Category || '').toLowerCase();
  let label = 'Catalyst';
  let icon = '⚡';

  if (sub.includes('order')) {
    label = 'Order Win';
    icon = '🎯';
  } else if (sub.includes('license') || sub.includes('approval') || sub.includes('regulatory')) {
    label = 'Approval';
    icon = '📋';
  } else if (sub.includes('preferential')) {
    label = 'Preferential';
    icon = '💰';
  } else if (sub.includes('qip')) {
    label = 'QIP';
    icon = '💰';
  } else if (sub.includes('rights')) {
    label = 'Rights Issue';
    icon = '📜';
  } else if (sub.includes('warrant')) {
    label = 'Warrants';
    icon = '📜';
  } else if (sub.includes('acquisition') || sub.includes('merger')) {
    label = 'M&A';
    icon = '🤝';
  } else if (sub.includes('fund')) {
    label = 'Fund Raise';
    icon = '💰';
  } else if (sub.includes('joint') || sub.includes('jv')) {
    label = 'Joint Venture';
    icon = '🤝';
  } else if (sub.includes('expansion') || sub.includes('capex')) {
    label = 'Capex';
    icon = '🏗️';
  } else if (sub.includes('promoter') || sub.includes('insider')) {
    label = 'Promoter Buy';
    icon = '🔥';
  }

  return { label, icon };
};

interface StockSymbolLabelProps {
  symbol: string;
  className?: string;
  showBadge?: boolean;
  showCatalyst?: boolean;
  showDealBadge?: boolean;
  emaPosture?: StockEmaPosture;
}

const StockSymbolLabel: React.FC<StockSymbolLabelProps> = ({
  symbol,
  className = '',
  showBadge = true,
  showCatalyst = true,
  showDealBadge = true,
  emaPosture,
}) => {
  const { isFnoSymbol } = useFnoSymbols();
  const { isNifty500Symbol } = useNifty500Symbols();
  const { volumeShockerSymbols } = useVolumeShockerSymbols();
  const { getPrimaryCatalyst } = useStockCatalysts();

  const isNifty500 = showBadge && isNifty500Symbol(symbol);
  const isFno = showBadge && isFnoSymbol(symbol);
  const isVolumeShocker = volumeShockerSymbols.has(symbol);

  // Corporate catalyst chip (Order Wins, Preferential Issue, M&A, Capex)
  const catalyst = showBadge && showCatalyst ? getPrimaryCatalyst(symbol) : null;
  const catalystBadge = catalyst ? getCatalystBadgeInfo(catalyst) : null;

  // Institutional Bulk & Block Deal Badge (ScanX-inspired Big Money Footprint)
  const resolvedDealBadge = useStockDealBadge(showBadge && showDealBadge ? symbol : null);
  const dealBadge = showBadge && showDealBadge ? resolvedDealBadge : null;

  const displaySymbol = symbol && symbol.startsWith('BSE_') ? `BSE: ${symbol.slice(4)}` : symbol;

  return (
    <span className={['stock-symbol-label', className].filter(Boolean).join(' ')}>
      <span className="stock-symbol-label__text">{displaySymbol}</span>
      {emaPosture && emaPosture.status !== 'below21Ema' ? (
        <span
          className={`stock-posture-dot stock-posture-dot--${emaPosture.tone}`}
          title={emaPosture.description}
          aria-label={emaPosture.description}
        />
      ) : null}
      {isVolumeShocker ? (
        <span
          className="stock-volume-shocker-icon"
          title="Volume above 2x the 30-day average"
          aria-label={`${symbol} volume is above 2x the 30-day average`}
        >
          <Zap size={13} fill="currentColor" />
        </span>
      ) : null}
      {isNifty500 ? (
        <span className="stock-badge stock-nifty500-badge" title="Part of Nifty 500" aria-label={`${symbol} is part of Nifty 500`}>
          N500
        </span>
      ) : null}
      {isFno ? (
        <span className="stock-badge stock-fno-badge" title="NSE F&O eligible" aria-label={`${symbol} is NSE F&O eligible`}>
          F&O
        </span>
      ) : null}
      {dealBadge ? (
        <span
          className={`stock-badge stock-deal-badge ${dealBadge.badgeClass}`}
          title={dealBadge.title}
          aria-label={dealBadge.title}
        >
          <span className="stock-deal-badge__icon" aria-hidden="true">{dealBadge.icon}</span>
          <span className="stock-deal-badge__label">{dealBadge.label}</span>
        </span>
      ) : null}
      {catalyst && catalystBadge ? (
        <span
          className="stock-badge stock-catalyst-badge"
          title={`Catalyst (${catalyst.DateFormatted}): [${catalyst.SubCategory || catalyst.Category}] ${catalyst.Headline}${catalyst.Amount ? ` · Amount: ${catalyst.Amount}` : ''}`}
          aria-label={`Catalyst: ${catalyst.Headline}`}
        >
          <span className="stock-catalyst-badge__icon" aria-hidden="true">{catalystBadge.icon}</span>
          <span className="stock-catalyst-badge__label">{catalystBadge.label}</span>
        </span>
      ) : null}
    </span>
  );
};

export default React.memo(StockSymbolLabel);
